/**
 * Authentication Service
 * Handles all auth-related API calls
 */

import { API_URL } from '../config/api';

// Token storage keys
const TOKEN_KEY = 'lumi_access_token';
const USER_KEY = 'lumi_user';
const PERSIST_KEY = 'lumi_persist_session';

/**
 * Get the active storage engine (localStorage if persisted, sessionStorage otherwise)
 */
const getStorage = () => {
  return localStorage.getItem(PERSIST_KEY) === 'true' ? localStorage : sessionStorage;
};

/**
 * Store auth token
 * @param {string} token - JWT access token
 * @param {boolean} persist - If true, use localStorage (survives browser close). Otherwise sessionStorage.
 */
export const setToken = (token, persist = true) => {
  // Clear from both storages first to avoid stale tokens
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);

  // Remember which storage we're using
  localStorage.setItem(PERSIST_KEY, String(persist));

  const storage = persist ? localStorage : sessionStorage;
  storage.setItem(TOKEN_KEY, token);
};

/**
 * Get auth token from the active storage
 */
export const getToken = () => {
  return getStorage().getItem(TOKEN_KEY);
};

/**
 * Remove auth token from all storages
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(PERSIST_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
};

/**
 * Store user data in the active storage
 */
export const setUser = (user) => {
  getStorage().setItem(USER_KEY, JSON.stringify(user));
};

/**
 * Get user data from the active storage
 */
export const getUser = () => {
  const user = getStorage().getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

/**
 * Get authorization header
 */
export const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Check if registration is allowed
 */
export const checkRegistrationStatus = async () => {
  try {
    const response = await fetch(`${API_URL}/auth/registration-status`, {
      method: 'GET',
    });

    if (!response.ok) {
      return { allowed: true, message: 'Registration is open' }; // Default to allowed on error
    }

    return await response.json();
  } catch (error) {
    console.warn('Failed to check registration status:', error);
    return { allowed: true, message: 'Registration is open' }; // Default to allowed on error
  }
};

/**
 * Register a new user
 */
export const register = async (email, password, name, height_cm) => {
  let response;
  try {
    response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, name, height_cm }),
    });
  } catch (err) {
    throw new Error('Cannot connect to server. Please check your connection and try again.');
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    throw new Error('Server returned an unexpected response. Please try again.');
  }

  if (!response.ok) {
    throw new Error(data.detail || 'Registration failed');
  }

  // Store token and user on successful registration
  if (data.access_token) {
    setToken(data.access_token);
    setUser(data.user);
  }

  return data;
};

/**
 * Login user
 */
export const login = async (email, password, rememberMe = true) => {
  // OAuth2 password flow uses form data
  const formData = new URLSearchParams();
  formData.append('username', email); // OAuth2 uses 'username' field
  formData.append('password', password);

  let response;
  try {
    response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });
  } catch (err) {
    throw new Error('Cannot connect to server. Please check your connection and try again.');
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    throw new Error('Server returned an unexpected response. Please try again.');
  }

  if (!response.ok) {
    throw new Error(data.detail || 'Invalid email or password');
  }

  // Store token and user data — persist based on "Remember Me" choice
  setToken(data.access_token, rememberMe);
  setUser({
    id: data.user_id,
    name: data.name,
    email: data.email,
  });

  return data;
};

/**
 * Logout user
 */
export const logout = async () => {
  const token = getToken();

  if (token) {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
        },
      });
    } catch (error) {
      console.warn('Logout API call failed:', error);
    }
  }

  // Always remove local storage regardless of API call success
  removeToken();
};

/**
 * Get current user info from API
 */
export const getCurrentUser = async () => {
  const token = getToken();

  if (!token) {
    return null;
  }

  const response = await fetch(`${API_URL}/auth/me`, {
    method: 'GET',
    headers: {
      ...getAuthHeader(),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      return null;
    }
    throw new Error('Failed to get user info');
  }

  const data = await response.json();
  setUser(data);
  return data;
};

/**
 * Update current user profile
 */
export const updateProfile = async (profileData) => {
  const response = await fetch(`${API_URL}/auth/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(profileData),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || 'Profile update failed');
  }

  setUser(data);
  return data;
};

/**
 * Request password reset
 */
export const forgotPassword = async (email) => {
  const response = await fetch(`${API_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || 'Password reset request failed');
  }

  return data;
};

/**
 * Reset password with token
 */
export const resetPassword = async (token, newPassword) => {
  const response = await fetch(`${API_URL}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, new_password: newPassword }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || 'Password reset failed');
  }

  return data;
};

/**
 * Validate current token
 */
export const validateToken = async () => {
  const token = getToken();

  if (!token) {
    return false;
  }

  try {
    const response = await fetch(`${API_URL}/auth/validate`, {
      method: 'GET',
      headers: {
        ...getAuthHeader(),
      },
    });

    return response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = () => {
  return !!getToken();
};

export default {
  register,
  login,
  logout,
  getCurrentUser,
  updateProfile,
  forgotPassword,
  resetPassword,
  validateToken,
  isAuthenticated,
  getToken,
  getAuthHeader,
  setToken,
  removeToken,
  getUser,
  setUser,
  checkRegistrationStatus,
};
